import {
  Package,
  Receipt,
  TriangleAlertIcon,
  Truck,
} from "@/components/icons";
import { Skeleton } from "@/components/kit";
import type { DashboardSummaryDTO } from "@/modules/dashboard/domain/types";
import { Link } from "@tanstack/react-router";

type DashboardStatsProps = {
  summary?: DashboardSummaryDTO;
  isLoading?: boolean;
};

function StatCard({
  icon: Icon,
  label,
  value,
  accentClass,
  href,
  search,
  tourId,
}: {
  icon: typeof Receipt;
  label: string;
  value: number;
  accentClass: string;
  href?: string;
  search?: Record<string, unknown>;
  tourId?: string;
}) {
  const content = (
    <div
      className="brand-card flex items-center gap-3 p-4 transition-colors hover:bg-muted/30"
      data-tourid={tourId}
    >
      <div
        className={`flex size-10 shrink-0 items-center justify-center rounded-xl ${accentClass}`}
      >
        <Icon />
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-muted-foreground text-xs">{label}</span>
        <span className="font-display text-2xl tabular-nums leading-none">
          {value}
        </span>
      </div>
    </div>
  );

  if (href) {
    return (
      <Link
        to={href}
        search={search}
        className="block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {content}
      </Link>
    );
  }

  return content;
}

export function DashboardStats({ summary, isLoading }: DashboardStatsProps) {
  if (isLoading) {
    return (
      <div
        className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
        data-tourid="dashboard-stats"
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[4.5rem] rounded-2xl" />
        ))}
      </div>
    );
  }

  return (
    <div
      className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
      data-tourid="dashboard-stats"
    >
      <StatCard
        icon={Receipt}
        label="ອໍເດີ້ມື້ນີ້"
        value={summary?.ordersToday ?? 0}
        accentClass="bg-primary/20 text-secondary"
        href="/app/orders"
        tourId="dashboard-stat-orders-today"
      />
      <StatCard
        icon={Package}
        label="ລໍຖ້າແພັກ"
        value={summary?.waitingToPack ?? 0}
        accentClass="bg-secondary/20 text-secondary"
        href="/app/fulfillment/pack"
        tourId="dashboard-stat-waiting-pack"
      />
      <StatCard
        icon={Truck}
        label="ພ້ອມສົ່ງ"
        value={summary?.readyToShip ?? 0}
        accentClass="bg-accent/30 text-foreground"
        href="/app/fulfillment"
        tourId="dashboard-stat-ready-ship"
      />
      <StatCard
        icon={TriangleAlertIcon}
        label="ສິນຄ້າໃກ້ໝົດ"
        value={summary?.lowStockCount ?? 0}
        accentClass="bg-destructive/15 text-destructive"
        href="/app/products"
        search={{ lowStock: true }}
        tourId="dashboard-stat-low-stock"
      />
    </div>
  );
}
